import { Injectable, isDevMode } from "@angular/core";
import { Action, State, StateContext } from "@ngxs/store";
import { DataLayerService } from "@vattenfall/core";
import { FinnishProductDetailsService } from "src/app/flows/shared/services/finnish-product-details.service";

import { MySelectElectricityProduct } from "./my-select-electricity-product.actions";
import { MySelectElectricityProductModel } from "./my-select-electricity-product.model";

export interface MySelectElectricityProductStateModel {
  [productId: string]: MySelectElectricityProductModel;
}

@State<MySelectElectricityProductStateModel>({
  name: "mySelectElectricityProduct",
})
@Injectable()
export class MySelectElectricityProductState {
  constructor(
    private finnishProductDetailsService: FinnishProductDetailsService,
    private dataLayerService: DataLayerService
  ) {}

  @Action(MySelectElectricityProduct.Fetch)
  async fetch(
    { dispatch }: StateContext<MySelectElectricityProductStateModel>,
    { productId, campaignCode }: MySelectElectricityProduct.Fetch
  ) {
    try {
      dispatch(new MySelectElectricityProduct.Loading(productId));
      const payload = await this.finnishProductDetailsService.get(productId, campaignCode).toPromise();
      dispatch(new MySelectElectricityProduct.Completed(productId, payload));
    } catch (error) {
      if (isDevMode()) console.error(error);
      dispatch(new MySelectElectricityProduct.Errorred(productId));
    }
  }

  @Action(MySelectElectricityProduct.Loading)
  loading(
    { patchState }: StateContext<MySelectElectricityProductStateModel>,
    { productId }: MySelectElectricityProduct.Loading
  ) {
    patchState({ [productId]: { loading: true } });
  }

  @Action(MySelectElectricityProduct.Completed)
  completed(
    { patchState }: StateContext<MySelectElectricityProductStateModel>,
    { productId, payload }: MySelectElectricityProduct.Completed
  ) {
    patchState({ [productId]: { loading: false, ...payload } });
    if (payload.productId && payload.agreementName && payload.productType)
      this.dataLayerService.ecommerce.detail({
        id: payload.productId,
        name: payload.productType,
        variant: payload.agreementName,
        price: payload.electricityPrice?.value,
      });
  }

  @Action(MySelectElectricityProduct.Errorred)
  errorred(
    { patchState }: StateContext<MySelectElectricityProductStateModel>,
    { productId }: MySelectElectricityProduct.Errorred
  ) {
    patchState({ [productId]: { loading: false, error: true } });
  }

  @Action(MySelectElectricityProduct.Selected)
  selected(
    { patchState, getState }: StateContext<MySelectElectricityProductStateModel>,
    {
      redirectUrl,
      productId,
      campaignCode,
      marketingCode,
      estimatedKwhConsumption,
      consumptionCode,
      agreementName,
      productType,
      electricityPrice,
    }: MySelectElectricityProduct.Selected
  ) {
    patchState({ [productId]: { ...getState()[productId], selected: true } });
    if (productId && agreementName && productType)
      this.dataLayerService.ecommerce.add({
        id: productId,
        name: productType,
        variant: agreementName,
        price: electricityPrice?.value,
      });
    const queryParameters = new URLSearchParams(
      Object.entries({
        consumptionCode,
        estimatedKwhConsumption: estimatedKwhConsumption.toString(),
        productId,
        campaignCode,
        marketingCode,
      }).filter(([_key, value]) => !!value) // Filter out undefined values.
    ).toString();

    window.location.href = `${redirectUrl}?${queryParameters}`;
  }
}
